import Autosuggest from '@components/autosuggest';
import Field from '@components/field';
import Select from '@components/select';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Checkbox } from 'components/Checkbox';
import { DropdownToggle } from 'components/DropdownToggle';
import { useStore } from 'hooks/useStore';
import { IFirmList } from 'store/Dictionaries/Dictionaries';
import { PopupIds } from 'store/UIStore/Popups';
import { interpolate } from 'utils/string/format';

import { FirmEmoji } from '../FirmEmoji';
import { QuickFirmsSearch } from '../QuickFirmsSearch';

import css from './Content.module.css';

enum listGroups {
  blackLists = 'blackLists',
  neutralLists = 'neutralLists',
  whiteLists = 'whiteLists',
}

const Content = () => {
  const {
    app: { i18n },
    ui: { popups },
    profile,
    filter: {
      firms: { ratingType, isExclusiveMode, firmGeo, toggleList, selectedLists, isSelectedListsDefault },
    },
    dictionaries: {
      data: { privateFirmLists },
    },
  } = useStore();

  const [active, setActive] = useState(false);

  const handleToggle = () => {
    setActive(!active);
  };

  const openNewFirmListPopup = () => {
    popups.open(PopupIds.firmListsPopup);
  };

  const activeFirmList = isExclusiveMode.option.value
    ? privateFirmLists.favExclusiveLists
    : privateFirmLists.favInclusiveLists;

  const activeDropdownFirmLists = isExclusiveMode.option.value
    ? [listGroups.blackLists, listGroups.neutralLists, listGroups.whiteLists]
    : [listGroups.whiteLists, listGroups.neutralLists, listGroups.blackLists];

  const dropdownButtonText = isSelectedListsDefault
    ? i18n.firms.allLists
    : interpolate(i18n.firms.moreLists, { count: selectedLists.length.toString() });

  return (
    <>
      <div className={css.separator}></div>
      {profile.data?.isPaidUser ? (
        <div className={css.firmsFilter}>
          <div className={css.innerColumn}>
            <Field className={css.field} size="medium">
              <Select
                isSearchable={false}
                options={ratingType.options}
                value={ratingType.option}
                onChange={ratingType.setOption}
              />
            </Field>

            <Field className={css.field} size="medium">
              <Autosuggest
                placeholder={i18n.firms.from}
                suggestions={firmGeo.suggestions}
                value={firmGeo.value}
                onChange={firmGeo.setValue}
                onClear={firmGeo.clear}
                onSuggestionSelected={firmGeo.onSuggestionSelected}
                onSuggestionsClearRequested={firmGeo.resetSuggestions}
                onSuggestionsFetchRequested={firmGeo.onFetchRequested}
              />
            </Field>

            <Field className={css.field} size="medium">
              <QuickFirmsSearch />
            </Field>
          </div>

          <div className={css.innerColumn}>
            <Field className={css.field} size="medium">
              <Select
                isSearchable={false}
                options={isExclusiveMode.options}
                value={isExclusiveMode.option}
                onChange={isExclusiveMode.setOption}
              />
            </Field>

            {privateFirmLists.totalLists > 0 && (
              <>
                <ul className={css.firmList}>
                  {activeFirmList.map((firm: IFirmList) => {
                    return (
                      <li key={firm.id} className={css.listItem}>
                        <Checkbox
                          small
                          checked={selectedLists.includes(firm.id)}
                          className={css.container}
                          disabled={false}
                          onChange={() => toggleList(firm.id)}
                        >
                          <span className={css.firmName}>{firm.name}</span>
                          <FirmEmoji className={css.emoji} emoji={firm.emoji} />
                        </Checkbox>
                      </li>
                    );
                  })}
                </ul>

                <div className={css.toolbar}>
                  <DropdownToggle
                    dropdown={
                      <ul className={cn('glz-dropdown-list', css.dropdown)}>
                        {activeDropdownFirmLists.map(listName => (
                          <li key={listName}>
                            <span className={css.firmListName}>{privateFirmLists[listName].name}</span>
                            <ul className={css.firmDropdownList}>
                              {privateFirmLists[listName].lists.map((firm: any) => (
                                <li key={firm.id} className={css.listItem}>
                                  <Checkbox
                                    small
                                    checked={selectedLists.includes(firm.id)}
                                    className={css.container}
                                    disabled={false}
                                    onChange={() => toggleList(firm.id)}
                                  >
                                    <span className={css.firmName}>{firm.name}</span>
                                    <FirmEmoji className={css.emoji} emoji={firm.emoji} />
                                  </Checkbox>
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    }
                    onToggle={handleToggle}
                  >
                    <DropdownToggle.DefaultButton gray active={active}>
                      {dropdownButtonText}
                    </DropdownToggle.DefaultButton>
                  </DropdownToggle>

                  <button
                    className="glz-link glz-is-pseudo glz-is-gray"
                    title={i18n.firms.createNewList}
                    onClick={openNewFirmListPopup}
                  >
                    {i18n.firms.createNew}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <p className={cn(css.onlyForPaid, 'glz-p glz-is-size-3 glz-is-bold')}>{i18n.firms.onlyForPaid}</p>
      )}
    </>
  );
};

const connected = observer(Content);

export { connected as Content };
